import dayjs, { Dayjs } from 'dayjs';
import { array, boolean, mixed, number, object, string } from 'yup';

import { YUP_ERRORS_MESSAGES } from '@shared/constants';
import { getRewardValueValidationSchema, validateObjectId } from '@shared/utils';

import { DailyTaskType, SpecialTaskType, UserRewardType, UserRole } from 'v2/constants';
import { FormReward } from 'v2/types';

export interface DailyTaskFormValues {
  description: string;
  taskX: number;
  isBonusBuy: boolean;
  betLimit: number;
  totalTaskX: number;
  lowerLimitX: number;
  lowerRangeX: number;
  upperRangeX: number;
  countTaskX: number;
  date: Dayjs;
  gameId: string;
  tournamentId: string;
  type: DailyTaskType;
  rewards: FormReward[];
  completedCountLimit: number;
  tasksToUnlock: number;
  referralsToUnlock: number;
  passwordToUnlock: string;
  goldenTicketId: string;
  allowedUserRoles: UserRole[];
  isSpecial: boolean;
  specialTaskType: SpecialTaskType;
  endDate: Dayjs;
  imageId: string | null;
}

export const DESCRIPTION_MAX_LENGTH = 150;

export const DAILY_TASK_FORM_INITIAL_VALUES: DailyTaskFormValues = {
  description: '',
  allowedUserRoles: [UserRole.DEFAULT_USER],
  taskX: 0,
  isBonusBuy: false,
  betLimit: 0,
  totalTaskX: 0,
  lowerLimitX: 0,
  lowerRangeX: 0,
  upperRangeX: 0,
  countTaskX: 0,
  date: dayjs(),
  gameId: '',
  tournamentId: '',
  type: DailyTaskType.X,
  rewards: [{ type: UserRewardType.BALANCE, value: '', count: 1 }],
  completedCountLimit: 0,
  tasksToUnlock: 0,
  referralsToUnlock: 0,
  passwordToUnlock: '',
  goldenTicketId: '',
  isSpecial: false,
  specialTaskType: SpecialTaskType.TASKS,
  endDate: dayjs().add(1, 'day'),
  imageId: null,
};

export const dailyTaskValidationSchema = object().shape({
  description: string().required(YUP_ERRORS_MESSAGES.REQUIRED),
  allowedUserRoles: array()
    .of(mixed().oneOf(Object.values(UserRole)).required(YUP_ERRORS_MESSAGES.REQUIRED))
    .min(1, YUP_ERRORS_MESSAGES.ROLES),
  date: mixed<Dayjs>().test('not null', YUP_ERRORS_MESSAGES.REQUIRED, (value) => !!value),
  endDate: mixed<Dayjs>().when('type', ([type], endDateSchema) => {
    if (type === DailyTaskType.TOP_X) {
      return endDateSchema.test('not null', YUP_ERRORS_MESSAGES.REQUIRED, (value) => !!value);
    } else {
      return endDateSchema;
    }
  }),
  taskX: number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === 0 ? undefined : value))
    .when('type', ([type], taskXSchema) => {
      if (type === DailyTaskType.X || type === DailyTaskType.COUNT_HIT_X) {
        return taskXSchema.required(YUP_ERRORS_MESSAGES.REQUIRED).min(1, 'Task X value must be greater than 0');
      } else {
        return taskXSchema;
      }
    }),
  isBonusBuy: boolean().when('type', ([type], isBonusBuySchema) => {
    if (type === DailyTaskType.X) {
      return isBonusBuySchema.required(YUP_ERRORS_MESSAGES.REQUIRED);
    } else {
      return isBonusBuySchema;
    }
  }),
  betLimit: number().when('type', ([type], betLimitSchema) => {
    if (
      type === DailyTaskType.X ||
      type === DailyTaskType.SUM_X ||
      type === DailyTaskType.SPECIFIC_X ||
      type === DailyTaskType.COUNT_HIT_X ||
      type === DailyTaskType.TOP_X
    ) {
      return betLimitSchema.required(YUP_ERRORS_MESSAGES.REQUIRED).min(1, 'Bet limit must be greater than 0');
    } else {
      return betLimitSchema;
    }
  }),
  countTaskX: number().when('type', ([type], countTaskXSchema) => {
    if (type === DailyTaskType.COUNT_HIT_X) {
      return countTaskXSchema
        .required(YUP_ERRORS_MESSAGES.REQUIRED)
        .min(1, 'Count task X value must be greater than 0');
    } else {
      return countTaskXSchema;
    }
  }),
  totalTaskX: number().when('type', ([type], totalTaskXSchema) => {
    if (type === DailyTaskType.SUM_X) {
      return totalTaskXSchema
        .required(YUP_ERRORS_MESSAGES.REQUIRED)
        .min(1, 'Total task X value must be greater than 0');
    } else {
      return totalTaskXSchema;
    }
  }),
  lowerLimitX: number().when('type', ([type], lowerLimitXSchema) => {
    if (type === DailyTaskType.SUM_X) {
      return lowerLimitXSchema
        .required(YUP_ERRORS_MESSAGES.REQUIRED)
        .min(1, 'Lower limit X value must be greater than 0');
    } else {
      return lowerLimitXSchema;
    }
  }),
  lowerRangeX: number().when('type', ([type], lowerRangeXSchema) => {
    if (type === DailyTaskType.SPECIFIC_X) {
      return lowerRangeXSchema
        .required(YUP_ERRORS_MESSAGES.REQUIRED)
        .min(1, 'Lower range X value must be greater than 0');
    } else {
      return lowerRangeXSchema;
    }
  }),
  upperRangeX: number().when('type', ([type], upperRangeXSchema) => {
    if (type === DailyTaskType.SPECIFIC_X) {
      return upperRangeXSchema
        .required(YUP_ERRORS_MESSAGES.REQUIRED)
        .min(1, 'Upper range X value must be greater than 0');
    } else {
      return upperRangeXSchema;
    }
  }),
  gameId: string().required(YUP_ERRORS_MESSAGES.REQUIRED),
  tournamentId: string()
    .test({
      message: YUP_ERRORS_MESSAGES.OBJECT_ID,
      test: validateObjectId,
    })
    .required(YUP_ERRORS_MESSAGES.OBJECT_ID),
  isSpecial: boolean(),
  specialTaskType: mixed().oneOf(Object.values(SpecialTaskType)),
  completedCountLimit: number().when('isSpecial', ([isSpecial], completedCountLimitSchema) => {
    if (isSpecial) {
      return completedCountLimitSchema
        .required(YUP_ERRORS_MESSAGES.REQUIRED)
        .min(1, 'Completed count limit must be greater than 0');
    } else {
      return completedCountLimitSchema;
    }
  }),
  tasksToUnlock: number().when(
    ['isSpecial', 'specialTaskType'],
    ([isSpecial, specialTaskType], tasksToUnlockLimitSchema) => {
      if (isSpecial && specialTaskType === SpecialTaskType.TASKS) {
        return tasksToUnlockLimitSchema
          .required(YUP_ERRORS_MESSAGES.REQUIRED)
          .min(0, 'Tasks to unlock must be greater or equal 0');
      } else {
        return tasksToUnlockLimitSchema;
      }
    }
  ),
  referralsToUnlock: number().when(
    ['isSpecial', 'specialTaskType'],
    ([isSpecial, specialTaskType], referralsToUnlockLimitSchema) => {
      if (isSpecial && specialTaskType === SpecialTaskType.REFERRALS) {
        return referralsToUnlockLimitSchema.required(YUP_ERRORS_MESSAGES.REQUIRED);
      } else {
        return referralsToUnlockLimitSchema;
      }
    }
  ),
  passwordToUnlock: string().when(
    ['isSpecial', 'specialTaskType'],
    ([isSpecial, specialTaskType], passwordToUnlockLimitSchema) => {
      if (isSpecial && specialTaskType === SpecialTaskType.PASSWORD) {
        return passwordToUnlockLimitSchema.required(YUP_ERRORS_MESSAGES.REQUIRED);
      } else {
        return passwordToUnlockLimitSchema;
      }
    }
  ),
  goldenTicketId: string().when(
    ['isSpecial', 'specialTaskType'],
    ([isSpecial, specialTaskType], goldenTicketUnlockLimitSchema) => {
      if (isSpecial && specialTaskType === SpecialTaskType.GOLDEN_TICKET) {
        return goldenTicketUnlockLimitSchema.required(YUP_ERRORS_MESSAGES.REQUIRED);
      } else {
        return goldenTicketUnlockLimitSchema;
      }
    }
  ),
  rewards: array().of(
    object().shape({
      type: mixed().oneOf(Object.values(UserRewardType)).required(YUP_ERRORS_MESSAGES.REQUIRED),
      value: getRewardValueValidationSchema(),
    })
  ),
  imageId: string()
    .test({
      message: YUP_ERRORS_MESSAGES.OBJECT_ID,
      test: validateObjectId,
    })
    .nullable(),
});
